import { cn } from '@/lib/utils/cn';

interface RiskGaugeProps {
  score: number;
  nivel: string;
  size?: 'sm' | 'md' | 'lg';
}

const levelColors: Record<string, { bg: string; text: string; ring: string }> = {
  BAJO: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    text: 'text-emerald-700 dark:text-emerald-400',
    ring: 'ring-emerald-500',
  },
  MEDIO: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-400',
    ring: 'ring-amber-500',
  },
  ALTO: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-700 dark:text-orange-400',
    ring: 'ring-orange-500',
  },
  CRITICO: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-400',
    ring: 'ring-red-500',
  },
};

export function RiskGauge({ score, nivel, size = 'md' }: RiskGaugeProps) {
  const colors = levelColors[nivel] || levelColors.MEDIO;
  const sizeClasses = {
    sm: 'w-20 h-20',
    md: 'w-32 h-32',
    lg: 'w-40 h-40',
  };
  const textSizes = {
    sm: 'text-xl',
    md: 'text-3xl',
    lg: 'text-4xl',
  };

  // SVG arc for gauge
  const radius = 45;
  const circumference = Math.PI * radius; // semicircle
  const progress = (score / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className={cn('relative', sizeClasses[size])}>
        <svg viewBox="0 0 100 60" className="w-full h-full">
          {/* Background arc */}
          <path
            d="M 5 55 A 45 45 0 0 1 95 55"
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            className="text-muted/20"
            strokeLinecap="round"
          />
          {/* Progress arc */}
          <path
            d="M 5 55 A 45 45 0 0 1 95 55"
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            className={colors.text}
            strokeLinecap="round"
            strokeDasharray={`${progress} ${circumference}`}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center pt-2">
          <span className={cn('font-bold', textSizes[size], colors.text)}>{score}</span>
        </div>
      </div>
      <div className={cn('px-3 py-1 rounded-full text-xs font-semibold', colors.bg, colors.text)}>
        Riesgo {nivel}
      </div>
    </div>
  );
}

// Barra de riesgo individual para desglose
interface RiskBarProps {
  label: string;
  puntos: number;
  max: number;
  detalle: string;
}

export function RiskBar({ label, puntos, max, detalle }: RiskBarProps) {
  const pct = max > 0 ? (puntos / max) * 100 : 0;
  const color = pct <= 33 ? 'bg-emerald-500' : pct <= 66 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground">
          {puntos}/{max}
        </span>
      </div>
      <div className="h-2 rounded-full bg-muted/30 overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-500', color)}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-xs text-muted-foreground">{detalle}</p>
    </div>
  );
}
